import { basicUIPlugin } from '@stackflow/plugin-basic-ui'
import '@stackflow/plugin-basic-ui/index.css'
import { basicRendererPlugin } from '@stackflow/plugin-renderer-basic'
import { ActivityComponentType, stackflow } from '@stackflow/react'
import { GetServerSideProps } from 'next'
import { useEffect, useMemo } from 'react'
import AIAccuracyActivity from '../../../../components/webview/lessons/AI/accuracy/AIAccuracyActivity'
import AIAccuracyMetricDetailActivity from '../../../../components/webview/lessons/AI/accuracy/AIAccuracyMetricDetailActivity'
import AILevelDescriptionActivity from '../../../../components/webview/lessons/AI/common/AILevelDescriptionActivity'
import AIRingleTipActivity from '../../../../components/webview/lessons/AI/common/AIRingleTipActivity'
import AIComplexityActivity from '../../../../components/webview/lessons/AI/complexity/AIComplexityActivity'
import AIComplexityMetricDetailActivity from '../../../../components/webview/lessons/AI/complexity/AIComplexityMetricDetailActivity'
import AIFluencyActivity from '../../../../components/webview/lessons/AI/fluency/AIFluencyActivity'
import AIHighlightDetailActivity from '../../../../components/webview/lessons/AI/highlights/AIHighlightDetailActivity'
import AIOverviewHighlightListActivity from '../../../../components/webview/lessons/AI/highlights/AIOverviewHighlightListActivity'
import AIStatisticHighlightDetailListActivity from '../../../../components/webview/lessons/AI/highlights/AIStatisticHighlightDetailListActivity'
import AIPronunciationActivity from '../../../../components/webview/lessons/AI/pronunciation/AIPronunciationActivity'
import AIStatisticActivity from '../../../../components/webview/lessons/AI/statistic/AIStatisticActivity'
import AIStatisticHighlightDetailActivity from '../../../../components/webview/lessons/AI/statistic/AIStatisticHighlightDetailActivity'
import AIStatisticHighlightListActivity from '../../../../components/webview/lessons/AI/statistic/AIStatisticHighlightListActivity'
import AIStatisticLessonListActivity from '../../../../components/webview/lessons/AI/statistic/AIStatisticLessonListActivity'
import LessonCancelActivity from '../../../../components/webview/lessons/LessonCancelActivity'
import LessonClassroomActivity from '../../../../components/webview/lessons/LessonClassroomActivity'
import LessonEnterBeforeActivity from '../../../../components/webview/lessons/LessonEnterBeforeActivity'
import LessonEvaluationActivity from '../../../../components/webview/lessons/LessonEvaluationActivity'
import LessonHomeActivity from '../../../../components/webview/lessons/LessonHomeActivity'
import LessonMaterialChangeActivity from '../../../../components/webview/lessons/LessonMaterialChangeActivity'
import LessonMaterialDetailActivity from '../../../../components/webview/lessons/LessonMaterialDetailActivity'
import LessonMaterialSearchActivity from '../../../../components/webview/lessons/LessonMaterialSearchActivity'
import LessonMaterialSearchListActivity from '../../../../components/webview/lessons/LessonMaterialSearchListActivity'
import LessonMaterialsCategoryActivity from '../../../../components/webview/lessons/LessonMaterialsCategoryActivity'
import LessonPreparationActivity from '../../../../components/webview/lessons/LessonPreparationActivity'
import LessonPreparationOverActivity from '../../../../components/webview/lessons/LessonPreparationOverActivity'
import LessonReminderActivity from '../../../../components/webview/lessons/LessonReminderActivity'
import LessonStyleActivity from '../../../../components/webview/lessons/LessonStyleActivity'
import MaterialViewActivity from '../../../../components/webview/lessons/MaterialViewActivity'
import ReviewDetailActivity from '../../../../components/webview/lessons/ReviewDetailActivity'
import ReviewLessonStyleActivity from '../../../../components/webview/lessons/ReviewLessonStyleActivity'
import TutorMatchingOptionChangeActivity from '../../../../components/webview/lessons/TutorMatchingOptionChangeActivity'
import AchievementsActivity from '../../../../components/webview/my-ringle/AchievementsActivity'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'

const activities = {
  LessonHomeActivity,
  LessonStyleActivity,
  LessonCancelActivity,
  LessonMaterialChangeActivity,
  LessonMaterialsCategoryActivity,
  LessonMaterialSearchListActivity,
  LessonMaterialSearchActivity,
  LessonMaterialDetailActivity,
  LessonReminderActivity,
  LessonPreparationActivity,
  LessonPreparationOverActivity,
  LessonEvaluationActivity,
  LessonEnterBeforeActivity,
  LessonClassroomActivity,
  MaterialViewActivity,
  TutorMatchingOptionChangeActivity,
  ReviewLessonStyleActivity,
  ReviewDetailActivity,
  AchievementsActivity,
  AIComplexityActivity,
  AIAccuracyActivity,
  AIFluencyActivity,
  AIPronunciationActivity,
  AILevelDescriptionActivity,
  AIComplexityMetricDetailActivity,
  AIAccuracyMetricDetailActivity,
  AIHighlightDetailActivity,
  AIStatisticHighlightDetailListActivity,
  AIStatisticActivity,
  AIStatisticLessonListActivity,
  AIStatisticHighlightListActivity,
  AIStatisticHighlightDetailActivity,
  AIRingleTipActivity,
  AIOverviewHighlightListActivity,
}
type SameKeyValue<T extends keyof typeof activities> = {
  [K in T]: K
}
const createActivitiesMap = <T extends keyof typeof activities>(
  acts: Record<T, ActivityComponentType>
): SameKeyValue<T> =>
  (Object.keys(acts) as T[])
    .map((activity) => ({ [activity]: activity }))
    .reduce((ac, cr) => {
      return {
        ...ac,
        ...cr,
      }
    }, {}) as SameKeyValue<T>

export const LESSON_ACTIVITIES_MAP = createActivitiesMap(activities)

export type LessonsActivityNamesType = keyof typeof activities

export const useLessonsStack = (initialActivityName?: string | string[], initialParams?) => {
  const {
    Stack: LessonsStack,
    useFlow: useLessonsFlow,
    activities: LessonsActivities,
  } = useMemo(() => {
    return stackflow({
      transitionDuration: 350,
      activities: activities,
      plugins: [
        basicRendererPlugin(),
        basicUIPlugin({
          theme: 'cupertino',
        }),
        () => {
          return {
            key: 'lessons-param-nested-navigation-plugin',
            overrideInitialEvents: ({ initialEvents }) => {
              const temp = {
                ...initialEvents[0],
                activityName: initialActivityName as LessonsActivityNamesType,
                activityParams: initialParams,
              }
              return [temp]
            },
          }
        },
      ],
      initialActivity: () => initialActivityName as LessonsActivityNamesType,
    })
  }, [initialActivityName])

  return { LessonsStack, LessonsActivities, useLessonsFlow }
}

const Index = ({ activity, lessonId, materialId, type, isReview, tab }) => {
  const { LessonsStack } = useLessonsStack(activity, {
    lessonId: lessonId,
    materialId: materialId,
    type: type,
    isReview: isReview,
    tab: tab,
  })
  const router = useAppRouter()
  const { sendPageViewEvent } = useEventTracking()

  useEffect(() => {
    if (!router) return
    console.log('SEND GTM EVENT TEST', type)
    console.log('SEND GTM EVENT TEST', router)

    switch (activity) {
      case 'LessonMaterialChangeActivity':
        if (materialId) {
          return sendPageViewEvent({ page_name: 'lesson_change_material' })
        } else {
          return sendPageViewEvent({ page_name: 'lesson_select_material' })
        }
      case 'LessonStyleActivity':
        return sendPageViewEvent({ page_name: 'lesson_style' })
      case 'LessonReminderActivity':
        return sendPageViewEvent({ page_name: 'lesson_prep_overview' })
      case 'LessonPreparationActivity':
        return sendPageViewEvent({ page_name: 'lesson_prep' })
      case 'LessonPreparationOverActivity':
        return sendPageViewEvent({ page_name: 'lesson_prep_done' })
    }
    // Native
    // sendPageViewEvent({ page_name: 'lesson_enter_classroom' })
    // sendPageViewEvent({ page_name: 'classroom' })
    // Component
    // sendPageViewEvent({ page_name: 'lesson_prep_exit_warining' })
    // sendPageViewEvent({ page_name: 'lesson_prep_edit_warining' })
    // sendPageViewEvent({ page_name: 'upcoming_lessons' })
    // sendPageViewEvent({ page_name: 'past_lessons' })
  }, [router.asPath])

  return (
    <div className="h-full w-full">
      <LessonsStack />
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (props) => {
  const { query } = props
  const {
    activity = 'LessonHomeActivity',
    lessonId = '',
    materialId = '',
    type = '',
    isReview = false,
    tab = 0,
  } = query
  return {
    props: {
      activity: activity,
      lessonId: lessonId,
      materialId: materialId,
      type: type,
      isReview: isReview,
      tab: tab,
    },
  }
}

export default Index
